import {
  Flex,
  Icon,
  IconButton,
  Image,
  Tag,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { formatToCurrency } from '@common/utils/formatter';
import isNil from 'lodash/isNil';
import {
  BathIcon,
  BedSingleIcon,
  CalendarIcon,
  PencilIcon,
  RulerIcon,
} from 'lucide-react';
import {
  DEFAULT_PROPERTY_IMAGE_URL,
  getPropertySource,
} from '~/apps/consumer/utils/listing.utils';
import { numberFormatter } from '~/common/utils/formatter';
import { EditOffMarketListingModal } from '../../../OffMarketListings/EditOffMarketListing/EditOffMarketListingModal';
import { useListingPageContext } from '../../ListingPageContext';
import { getPropertyDetails } from '../../utils';

export const ListingInfoMobile = () => {
  const {
    listing,
    propertyDetails,
    isViewOnly,
    refetchPropertyDetails,
    refetchListingReport,
  } = useListingPageContext();
  const {
    photos,
    streetAddress,
    secondAddressLine,
    bedrooms,
    bathrooms,
    area,
    daysOnMarket,
    listPrice,
    status,
  } = getPropertyDetails(listing, propertyDetails);
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Flex alignItems="center" px={4}>
      <Image
        borderRadius="4px"
        fallbackSrc={DEFAULT_PROPERTY_IMAGE_URL}
        height="80px"
        src={getPropertySource(photos?.[0])}
        width="110px"
      />
      <VStack alignItems="flex-start" minWidth="0" px={2} spacing={0}>
        <Flex alignItems="center" gap={2}>
          <Text fontSize="lg" fontWeight="semibold" lineHeight="1.2">
            {formatToCurrency(listPrice)}
          </Text>
          <Tag bg="whiteAlpha.300" color="white" size="sm" textTransform="none">
            {status}
          </Tag>
          {!!propertyDetails && !isViewOnly && (
            <IconButton
              aria-label="Edit off-market listing"
              icon={<Icon as={PencilIcon} boxSize={4} color="white" />}
              size="xs"
              variant="ghost"
              onClick={onOpen}
            />
          )}
        </Flex>
        <Text
          fontSize="sm"
          fontWeight="medium"
          lineHeight="1.2"
          overflow="hidden"
          p={0}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          width="100%"
        >
          {streetAddress ?? ''}
        </Text>
        <Text color="whiteAlpha.700" fontSize="sm" lineHeight="1.2">
          {secondAddressLine ?? ''}
        </Text>
        <Flex
          gap={2}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {!isNil(bedrooms) && (
            <Flex alignItems="center" gap={0.5}>
              <Icon as={BedSingleIcon} boxSize={3} color="whiteAlpha.700" />
              <Text color="whiteAlpha.700" fontSize="sm">
                {bedrooms}
              </Text>
            </Flex>
          )}
          {!isNil(bathrooms) && (
            <Flex alignItems="center" gap={0.5}>
              <Icon as={BathIcon} boxSize={3} color="whiteAlpha.700" />
              <Text color="whiteAlpha.700" fontSize="sm">
                {bathrooms}
              </Text>
            </Flex>
          )}
          {!isNil(area) && (
            <Flex alignItems="center" gap={0.5}>
              <Icon as={RulerIcon} boxSize={3} color="whiteAlpha.700" />
              <Text color="whiteAlpha.700" fontSize="sm">
                {numberFormatter(area, { maxPrecision: 0 })} sqft
              </Text>
            </Flex>
          )}
          {!isNil(daysOnMarket) && (
            <Flex alignItems="center" gap={0.5}>
              <Icon as={CalendarIcon} boxSize={3} color="whiteAlpha.700" />
              <Text color="whiteAlpha.700" fontSize="sm">
                {daysOnMarket}
                {' d'}
              </Text>
            </Flex>
          )}
        </Flex>
      </VStack>
      {!!propertyDetails && !isViewOnly && (
        <EditOffMarketListingModal
          isOpen={isOpen}
          propertyDetails={propertyDetails}
          onClose={onClose}
          onSave={() => {
            void (async () => {
              await refetchPropertyDetails();
              await refetchListingReport();
            })();
          }}
        />
      )}
    </Flex>
  );
};
